import { useCallback, useEffect, useState } from 'react';
import { hrApi } from '@/api';
import { EmployeeSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { FormGrid } from '@/components/ui/FormGrid';
import { Select } from '@/components/ui/Select';
import { dataTablePageProps, usePagination } from '@/hooks/usePagination';
import { useEmployeeOptions } from '@/hooks/useEmployeeOptions';
import type { HrAttendance } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function HrAttendancePage() {
  const pagination = usePagination();
  const { getLabel } = useEmployeeOptions();
  const [data, setData] = useState<HrAttendance[]>([]);
  const [loading, setLoading] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [checkInMode, setCheckInMode] = useState('MANUAL');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrApi.listAttendance({ page: pagination.page, size: pagination.size });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const markAttendance = async () => {
    if (!employeeId) {
      notify.error('Select an employee');
      return;
    }
    setSubmitting(true);
    try {
      await hrApi.markAttendance({
        employeeId: Number(employeeId),
        checkIn: checkIn || undefined,
        checkOut: checkOut || undefined,
        checkInMode,
      });
      notify.success('Attendance recorded');
      setEmployeeId('');
      setCheckIn('');
      setCheckOut('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to mark attendance');
    } finally {
      setSubmitting(false);
    }
  };

  const columns: Column<HrAttendance>[] = [
    { key: 'id', header: '#' },
    { key: 'employeeId', header: 'Employee', render: (row) => getLabel(row.employeeId, true) },
    { key: 'checkIn', header: 'Check in' },
    { key: 'checkOut', header: 'Check out' },
    { key: 'checkInMode', header: 'Mode' },
    { key: 'workedMinutes', header: 'Minutes' },
  ];

  return (
    <div>
      <PageHeader
        title="Attendance"
        description="Manual, web, mobile, QR, and device punches with shift linkage"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Attendance' }]}
      />
      <div className="mb-6 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
        <h3 className="mb-2 text-sm font-medium">Mark attendance</h3>
        <FormGrid columns={4} className="items-end">
          <EmployeeSelect value={employeeId} onChange={setEmployeeId} />
          <DateTimePicker label="Check in" value={checkIn} onChange={setCheckIn} />
          <DateTimePicker label="Check out" value={checkOut} onChange={setCheckOut} />
          <Select
            label="Mode"
            value={checkInMode}
            onChange={(v) => setCheckInMode(v)}
            options={[
              { value: 'MANUAL', label: 'MANUAL' },
              { value: 'WEB', label: 'WEB' },
              { value: 'MOBILE', label: 'MOBILE' },
              { value: 'QR', label: 'QR' },
            ]}
          />
          <Button loading={submitting} onClick={() => void markAttendance()}>
            Record
          </Button>
        </FormGrid>
      </div>
      <DataTable columns={columns} data={data} loading={loading} keyField="id" {...dataTablePageProps(pagination)} />
    </div>
  );
}
